import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Stack,
  Tooltip,
  LinearProgress,
  Grid,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  Insights as InsightsIcon,
  ViewModule as ViewModuleIcon,
  Security as SecurityIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { DashboardProvider, useDashboard } from './DashboardContext';
import { formatDashboardDate, parseDashboardDateInput } from './dashboardHelpers';
import KPICards from './components/KPICards';
import SalesChart from './components/SalesChart';
import DebtOverview from './components/DebtOverview';
import InventoryOverview from './components/InventoryOverview';
import PaymentSummary from './components/PaymentSummary';
import PaymentBreakdown from './components/PaymentBreakdown';
import RecentTransactions from './components/RecentTransactions';
import ActivityTimeline from './components/ActivityTimeline';
import MovementSummary from './components/MovementSummary';
import SalesBreakdown from './components/SalesBreakdown';
import ProfitDrilldown from './components/ProfitDrilldown';
import TopSellingItems from './components/TopSellingItems';
import SupplierPerformance from './components/SupplierPerformance';
import SupplierPaymentSummary from './components/SupplierPaymentSummary';
import SupplierPayablesOverview from './components/SupplierPayablesOverview';
import CreditRiskAlerts from './components/CreditRiskAlerts';
import WalletOverview from './components/WalletOverview';
import AdvancedDashboard from './components/advanced/AdvancedDashboard';
import UsersActivity from './components/UsersActivity';
import DashboardStaleAlert from './components/DashboardStaleAlert';
import { DashboardInfoTip } from './components/DashboardInfoTip';
import { useTranslation } from 'react-i18next';
import { useAppSocket } from '../../providers/AppSocketContext';
import { useAuth } from '../../hooks/useAuth';
import { canRead, canSeeAdvancedDashboard, canSeeWhoIsSignedIn, canViewOperationField } from '../../util/access';
import ReportPageHeader from '../../components/ReportPageHeader';

const DashboardContent = () => {
  const {
    dateRange,
    setDateRange,
    viewMode,
    setViewMode,
    loading,
    sectionLoading,
    revalidating,
    staleDataAvailable,
    cachedAt,
    refresh,
    applyPendingRefresh,
    dismissStaleAlert,
  } = useDashboard();
  const { t } = useTranslation();
  const { session, access } = useAuth();
  const { appSettings } = useAppSocket();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';
  const canViewAdvanced = canSeeAdvancedDashboard(access);
  const canViewSupplyCost = canViewOperationField(access, 'supply', 'cost');
  const canViewItemCost = canRead(access, 'item', 'cost_price');
  const canViewUsersActivity =
    canSeeWhoIsSignedIn(access)
    || canRead(access, 'admin_portal')
    || canViewAdvanced;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleFullscreenToggle = useCallback(async () => {
    if (!document.fullscreenElement) {
      try {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } catch (err) {
        console.error('Fullscreen error:', err);
      }
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const handleFullscreenChange = useCallback(() => {
    setIsFullscreen(!!document.fullscreenElement);
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [handleFullscreenChange]);

  useEffect(() => {
    if (!canViewAdvanced && viewMode === 'advanced') {
      setViewMode('simple');
    }
    if (!canViewUsersActivity && viewMode === 'users') {
      setViewMode('simple');
    }
  }, [canViewAdvanced, canViewUsersActivity, setViewMode, viewMode]);

  return (
    <Box
      ref={containerRef}
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        bgcolor: isFullscreen ? 'background.default' : 'transparent',
        overflow: 'auto',
        height: isFullscreen ? '100vh' : 'auto',
        maxHeight: isFullscreen ? '100vh' : 'calc(100vh - 140px)',
      }}
    >
      <ReportPageHeader
        title={t('common:dashboard.title')}
        icon={<DashboardIcon />}
        period={{ from: dateRange.from, to: dateRange.to }}
        lastUpdated={cachedAt}
        subtitle={
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            sx={{ mt: 0.75 }}
          >
            <ToggleButton value="simple" sx={{ textTransform: 'none', gap: 0.25, px: 1.5 }}>
              <ViewModuleIcon fontSize="small" />
              {t('common:dashboard.viewMode.simple')}
              <DashboardInfoTip helpKey="viewMode.simple" />
            </ToggleButton>
            <ToggleButton value="timeline" sx={{ textTransform: 'none', gap: 0.25, px: 1.5 }}>
              <TimelineIcon fontSize="small" />
              {t('common:dashboard.viewMode.timeline')}
              <DashboardInfoTip helpKey="viewMode.timeline" />
            </ToggleButton>
            {canViewAdvanced && (
              <ToggleButton value="advanced" sx={{ textTransform: 'none', gap: 0.25, px: 1.5 }}>
                <InsightsIcon fontSize="small" />
                {t('common:dashboard.viewMode.advanced')}
                <DashboardInfoTip helpKey="viewMode.advanced" />
              </ToggleButton>
            )}
            {canViewUsersActivity && (
              <ToggleButton value="users" sx={{ textTransform: 'none', gap: 0.25, px: 1.5 }}>
                <SecurityIcon fontSize="small" />
                {t('common:dashboard.viewMode.users')}
                <DashboardInfoTip helpKey="viewMode.users" />
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        }
        actions={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              p: 1.25,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <TextField
              type="date"
              size="small"
              label={t('common:dashboard.from')}
              value={formatDashboardDate(dateRange.from)}
              onChange={(e) => {
                const d = parseDashboardDateInput(e.target.value);
                if (d) setDateRange({ ...dateRange, from: d });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 155 }}
            />
            <TextField
              type="date"
              size="small"
              label={t('common:dashboard.to')}
              value={formatDashboardDate(dateRange.to)}
              onChange={(e) => {
                const d = parseDashboardDateInput(e.target.value);
                if (d) setDateRange({ ...dateRange, to: d });
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 155 }}
            />
            <Tooltip title={t('common:dashboard.refresh')}>
              <IconButton onClick={refresh} disabled={loading} size="small" sx={{ color: themeColor }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFullscreen ? t('common:dashboard.exitFullscreen') : t('common:dashboard.fullscreen')}>
              <IconButton onClick={handleFullscreenToggle} size="small" sx={{ color: themeColor }}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      <DashboardStaleAlert
        open={staleDataAvailable}
        cachedAt={cachedAt}
        revalidating={revalidating}
        onRefresh={applyPendingRefresh}
        onDismiss={dismissStaleAlert}
      />

      <Fade in={Object.values(sectionLoading).some(Boolean)}>
        <LinearProgress
          sx={{
            mb: 2,
            borderRadius: 1,
            '& .MuiLinearProgress-bar': { bgcolor: themeColor },
          }}
        />
      </Fade>

      <Fade in={revalidating && !loading}>
        <LinearProgress
          sx={{
            mb: 2,
            borderRadius: 1,
            height: 3,
            opacity: 0.7,
            '& .MuiLinearProgress-bar': { bgcolor: themeColor },
          }}
        />
      </Fade>

      <Stack spacing={3}>
        {viewMode === 'users' && canViewUsersActivity ? (
          <UsersActivity />
        ) : viewMode === 'timeline' ? (
          <ActivityTimeline />
        ) : (
          <>
            <KPICards />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 8 }}>
                <SalesChart />
              </Grid>
              <Grid size={{ xs: 12, lg: 4 }}>
                <PaymentSummary />
              </Grid>
            </Grid>

            {canViewItemCost && <ProfitDrilldown />}
            <SalesBreakdown />
            <PaymentBreakdown />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 7 }}>
                <TopSellingItems />
              </Grid>
              <Grid size={{ xs: 12, lg: 5 }}>
                <CreditRiskAlerts />
              </Grid>
            </Grid>

            <DebtOverview />
            <WalletOverview />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 7 }}>
                <InventoryOverview />
              </Grid>
              {canViewSupplyCost && (
                <Grid size={{ xs: 12, lg: 5 }}>
                  <SupplierPerformance />
                </Grid>
              )}
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <SupplierPaymentSummary />
              </Grid>
              {canViewSupplyCost && (
                <Grid size={{ xs: 12, lg: 6 }}>
                  <SupplierPayablesOverview />
                </Grid>
              )}
            </Grid>

            <MovementSummary />
            <RecentTransactions />

            {canViewAdvanced && viewMode === 'advanced' && (
              <>
                <Divider sx={{ my: 1 }} />
                <AdvancedDashboard />
              </>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

const Dashboard = () => {
  return (
    <DashboardProvider>
      <DashboardContent />
    </DashboardProvider>
  );
};

export default Dashboard;